import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  fechaEnNicaragua,
  formatFxRate,
  formatMoney,
  formatQuantity,
  fromUnits,
  fxRate,
  hoy,
  idDeCamion,
  type Centavos,
  type Milli,
  type Settings,
} from '@abastece/shared';
import { utilidadDeVenta, type Cliente, type Devolucion, type FormaPagoVenta, type Pedido, type Producto, type UnidadVenta, type Usuario, type VentaPago } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { factoresDe, hayEnLaTienda, pedidosCortos } from '../lib/apartados.js';
import { emitirYVer } from '../lib/emitir.js';
import { imprimirTexto } from '../lib/imprimir.js';
import {
  canceladosEnOrden,
  cobradoComoSePidio,
  diasDeAtraso,
  entregadosEnOrden,
  formasDeLaVenta,
  marcaDeEntrega,
  pendientesEnOrden,
  totalConEnvio,
} from '../lib/pedidos.js';
import { instante } from '../lib/detalleEvento.js';
import { datosNegocio, enlaceWhatsApp, textoPedido } from '../lib/recibo.js';
import {
  FORMAS_DE_PAGO,
  SIN_FILTROS_PEDIDOS,
  filtrosDePedidos,
  pedidoCoincide,
  rangoDe,
  type ContextoPedidos,
  type FiltroPedidos,
} from '../lib/historial.js';
import { usePuede } from '../lib/sesion.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Interruptor } from '../ui/Campo.js';
import { BuscarEnLista, Desplegable, FilaDesplegables, FiltroFechas, FiltrosEnLinea, claveDeForma } from '../ui/FiltrosHistorial.js';
import { diaDeLista } from '../ui/FilasDeDinero.js';
import { Ico } from '../ui/Iconos.js';
import { Miniatura, MiniaturaIcono } from '../ui/Miniatura.js';
import { Modal } from '../ui/Modal.js';
import { PedirDato } from '../ui/Preguntar.js';
import { useT, type Clave } from '../i18n/index.js';
import { etiquetaLugar, etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { DetalleVenta } from './Ventas.js';

type Vista = 'pendientes' | 'entregados' | 'cancelados';

const VISTAS: readonly { vista: Vista; nombre: Clave }[] = [
  { vista: 'pendientes', nombre: 'pedidos.vistaPendientes' },
  { vista: 'entregados', nombre: 'pedidos.vistaEntregados' },
  { vista: 'cancelados', nombre: 'pedidos.vistaCancelados' },
];

/** How many of the delivered or the cancelled show at a time: the latest, and «Ver más» for the rest. */
const LOTE = 100;

/**
 * Los pedidos: the list (the owner, 2026-10-04). What is still to deliver first, the soonest due on
 * top, the late ones and today's marked; each says who, when, how much and what for, and «Cobrar»
 * takes it to the till loaded, as the till's own «Pedidos por entregar» does. The owner, a cashier
 * and a driver each charge from their own phone, into their own till. A new order is taken on a
 * screen of its own, «Nuevo pedido», which comes back here with it. The delivered say what the sale
 * says, its ticket, its day, who charged it and how it was paid; the cancelled, why. Each list is
 * searched and filtered as the sales are (`lib/historial.ts`): by client, product, note, ticket or why
 * it was cancelled, by the day it shows, and the delivered by how they were paid, who charged them and
 * a delivery cost.
 *
 * With `apartarPedidos` on (2026-10-08), each order still to deliver says whether the shop's stock
 * covers it, «Apartado», or what it is short of: a sale the owner let through, or a till that had
 * not heard of the order yet, takes from the orders taken last.
 *
 * Each still to deliver says what the customer pays, the delivery in it and on a line of its own,
 * and has «Recibo» (the owner, 2026-10-12: «print a receipt for an order without having to complete
 * the charge process»): the order's receipt, in a sheet, to print or send over WhatsApp.
 *
 * Each delivered one opens (2026-10-14) on everything about it, in a sheet (`DetallePedido`).
 *
 * Each still to deliver has «Editar» (the owner, 2026-10-15: «We need to be able to edit orders in
 * the order screen»): the screen it was taken on, as it stands (`routes/EditarPedido.tsx`), which
 * comes back here with it marked.
 */
export function Pedidos() {
  const t = useT();
  const nombre = useNombreProducto();
  const navigate = useNavigate();
  const { vista = 'pendientes', tomado } = useSearch({ from: '/pedidos' });
  const clientes = useLiveQuery(() => db.clientes.toArray(), [], []);
  const pedidos = useLiveQuery(() => db.pedidos.toArray(), [], []);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const puede = usePuede('gestionar_clientes');
  const puedeCobrar = usePuede('vender');
  const [cancelando, setCancelando] = useState<string | null>(null);
  /** Why the order was not cancelled: charged meanwhile, or cancelled on another device. It went unsaid. */
  const [noSeCancelo, setNoSeCancelo] = useState<string | null>(null);
  /** The order whose receipt is in a sheet over the list. */
  const [recibo, setRecibo] = useState<string | null>(null);
  /** The delivered order open in a sheet over the list. */
  const [abierto, setAbierto] = useState<string | null>(null);
  const [cuantos, setCuantos] = useState(LOTE);
  const [filtro, setFiltro] = useState<FiltroPedidos>(SIN_FILTROS_PEDIDOS);
  const cambiar = (cambio: Partial<FiltroPedidos>) => {
    setFiltro((f) => ({ ...f, ...cambio }));
    setCuantos(LOTE);
  };
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  /** The orders the stock no longer covers, and what each is short of; null while nothing is set aside. */
  const cortos = config.apartarPedidos
    ? pedidosCortos({ pedidos, factorDe: factoresDe(unidades), hayDe: hayEnLaTienda(existencias) })
    : null;

  /**
   * The sales behind the delivered, read only to show them: each one's ticket, day and payments,
   * and who charged it, from the sale's own event, as «Ventas rápidas» says whose hands it was.
   */
  const entregas = useLiveQuery(
    async () => {
      if (vista !== 'entregados') return null;
      const lista = await db.pedidos.where('estado').equals('entregado').toArray();
      const ids = lista.flatMap((p) => (p.ventaId ? [p.ventaId] : []));
      const ventas = new Map((await db.ventas.bulkGet(ids)).flatMap((v) => (v ? [[v.id, v] as const] : [])));
      const faltan = new Set(ids);
      const quien = new Map<string, string>();
      if (faltan.size > 0) {
        await db.eventos
          .where('type')
          .equals('venta.registrada')
          .reverse()
          .until(() => faltan.size === 0)
          .each((e) => {
            const id = (e.payload as { ventaId?: string }).ventaId;
            if (id && faltan.delete(id)) quien.set(id, e.actorId);
          });
      }
      const usuarios = new Map((await db.usuarios.toArray()).map((u) => [u.id, u.nombre]));
      return { ventas, quien, usuarios };
    },
    [vista],
    null,
  );

  const hoyEs = hoy();
  const pendientes = pendientesEnOrden(pedidos);
  const entregados = entregadosEnOrden(pedidos, entregas?.ventas ?? new Map());
  const cancelados = canceladosEnOrden(pedidos);
  const visibles = vista === 'pendientes' ? pendientes : vista === 'entregados' ? entregados : cancelados;
  const rango = rangoDe(filtro.periodo, hoyEs, filtro);
  const puestos = filtrosDePedidos(filtro, rango, vista === 'entregados');
  const filtrando = filtro.texto.trim() !== '' || puestos.length > 0;
  const contexto = useMemo<ContextoPedidos>(
    () => ({
      clientes: new Map(clientes.map((c) => [c.id, c.nombre])),
      productos: new Map(productos.map((p) => [p.id, p])),
      ventas: entregas?.ventas,
      quien: entregas?.quien,
    }),
    [clientes, productos, entregas],
  );
  const encontrados = filtrando ? visibles.filter((p) => pedidoCoincide(p, filtro, rango, contexto)) : visibles;
  // The delivered at what the sale charged, rounding and delivery and all; the rest at what was taken, its delivery too.
  const totalEncontrado = encontrados.reduce(
    (a, p) => a + ((p.ventaId ? entregas?.ventas.get(p.ventaId)?.total : undefined) ?? totalConEnvio(p)),
    0,
  );
  // Nothing said about the delivered while their sales are read: «nothing matches» was only «not read yet».
  const leyendo = vista === 'entregados' && filtrando && entregas === null;

  // The order just taken, in sight once it is drawn: it may be due later than a screenful of others.
  const visto = useRef(false);
  const alTomado = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (visto.current || !alTomado.current) return;
    visto.current = true;
    alTomado.current.scrollIntoView({ block: 'nearest' });
  });

  const clienteDe = (p: Pedido) => clientes.find((c) => c.id === p.clienteId)?.nombre ?? t('pedidos.clienteDesconocido');
  const nombreDe = (productoId: string) => {
    const p = productos.find((x) => x.id === productoId);
    return p ? nombre(p) : t('pedidos.productoNoEncontrado');
  };
  /** So much of a product, in the unit it is counted in, as «Inventario» says it. */
  const enSuUnidad = (cantidad: Milli, productoId: string) => {
    const p = productos.find((x) => x.id === productoId);
    return p ? `${formatQuantity(cantidad)} ${etiquetaUnidad(p.unidadBase, p.unidadBase)}` : formatQuantity(cantidad);
  };
  const formaDePago = (forma: FormaPagoVenta) => t(claveDeForma(forma));
  const enRecibo = recibo ? pedidos.find((p) => p.id === recibo) : undefined;
  return (
    <Pagina
      titulo={t('pedidos.titulo')}
      subtitulo={t('pedidos.pendientes', { n: pendientes.length })}
      acciones={
        puede && (
          <Link to="/clientes/pedidos/nuevo" className="btn-primario gap-1.5 px-4 text-sm" data-guia="pedidos-nuevo">
            <Ico icono="nuevo" className="size-4" />
            {t('pedidos.nuevo')}
          </Link>
        )
      }
    >
      {noSeCancelo && (
        <p role="alert" className="mb-4 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-pedido-no-cancelado>
          {t('pedidos.noSeCancelo', { motivo: noSeCancelo })}
        </p>
      )}
      <div className="mb-4 flex gap-1 rounded-xl bg-slate-800 p-1" data-pedidos-vistas>
        {VISTAS.map((v) => (
          <button
            key={v.vista}
            type="button"
            onClick={() => {
              setCuantos(LOTE);
              void navigate({ to: '/pedidos', search: v.vista === 'pendientes' ? {} : { vista: v.vista }, replace: true });
            }}
            data-sin-icono="pestana"
            aria-pressed={vista === v.vista}
            className={`min-w-0 flex-1 rounded-lg px-2 py-2 text-sm transition max-[359px]:px-1.5 max-[359px]:text-xs ${
              vista === v.vista ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
            }`}
          >
            {t(v.nombre)}
          </button>
        ))}
      </div>

      {(visibles.length > 0 || filtrando) && (
        <FiltrosEnLinea
          buscar={{ texto: filtro.texto, onTexto: (texto) => cambiar({ texto }), placeholder: t('historial.buscarPedidos') }}
          hayPuestos={puestos.length > 0}
          onQuitarTodos={() => cambiar({ ...SIN_FILTROS_PEDIDOS, texto: filtro.texto })}
        >
          <FiltroFechas
            periodo={filtro.periodo}
            desde={filtro.desde}
            hasta={filtro.hasta}
            ayuda={t(vista === 'entregados' ? 'historial.porCobro' : 'historial.porEntrega')}
            onCambio={cambiar}
          />
          <BuscarEnLista
            clave="clienteId"
            etiqueta={t('historial.cliente')}
            placeholder={t('historial.buscarCliente')}
            opciones={clientes}
            value={filtro.clienteId}
            onChange={(clienteId) => cambiar({ clienteId })}
          />
          {/* Read off the sale that charged each: only the delivered have one. */}
          {vista === 'entregados' && (
            <>
              <FilaDesplegables>
                <Desplegable
                  clave="forma"
                  etiqueta={t('historial.forma')}
                  value={filtro.forma}
                  onChange={(v) => cambiar({ forma: v as FormaPagoVenta | '' })}
                  elegido={filtro.forma ? formaDePago(filtro.forma) : undefined}
                >
                  <option value="">{t('historial.todas')}</option>
                  {FORMAS_DE_PAGO.map((f) => (
                    <option key={f} value={f}>
                      {formaDePago(f)}
                    </option>
                  ))}
                </Desplegable>
                <Desplegable
                  clave="quien"
                  etiqueta={t('historial.quien')}
                  value={filtro.quien}
                  onChange={(quien) => cambiar({ quien })}
                  elegido={
                    filtro.quien
                      ? t('historial.cobro', { nombre: usuarios.find((u) => u.id === filtro.quien)?.nombre ?? t('ventas.alguien') })
                      : undefined
                  }
                >
                  <option value="">{t('historial.todos')}</option>
                  {[...usuarios]
                    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.nombre}
                      </option>
                    ))}
                </Desplegable>
              </FilaDesplegables>
              <Interruptor etiqueta={t('historial.conEnvio')} checked={filtro.conEnvio} onChange={(conEnvio) => cambiar({ conEnvio })} />
            </>
          )}
        </FiltrosEnLinea>
      )}

      {filtrando && !leyendo && encontrados.length > 0 && (
        <p className="tabular mb-3 text-sm text-slate-400" data-historial-resumen>
          {t('historial.encontrados', { n: encontrados.length, total: formatMoney(totalEncontrado as Centavos) })}
        </p>
      )}

      {leyendo ? null : encontrados.length === 0 ? (
        filtrando ? (
          <Vacio mensaje={t('historial.ninguno')} icono="buscar">
            <button type="button" className="btn-secundario" onClick={() => cambiar(SIN_FILTROS_PEDIDOS)}>
              <Ico icono="reiniciar" />
              {t('historial.quitarBusqueda')}
            </button>
          </Vacio>
        ) : (
          <Vacio
            mensaje={t(vista === 'pendientes' ? 'pedidos.ninguno' : vista === 'entregados' ? 'pedidos.ningunoEntregado' : 'pedidos.ningunoCancelado')}
            icono="pedidos"
          />
        )
      ) : (
        <ul className="flex flex-col gap-2">
          {encontrados.slice(0, vista === 'pendientes' ? undefined : cuantos).map((p) => {
            if (vista === 'entregados') {
              const venta = p.ventaId ? entregas?.ventas.get(p.ventaId) : undefined;
              const quienId = p.ventaId ? entregas?.quien.get(p.ventaId) : undefined;
              const quien = (quienId ? entregas?.usuarios.get(quienId) : undefined) ?? t('ventas.alguien');
              return (
                <li key={p.id} data-pedido={p.id}>
                  {/* The whole card opens it: everything about the order and its sale (2026-10-14). */}
                  <button
                    type="button"
                    onClick={() => setAbierto(p.id)}
                    data-sin-icono="fila"
                    data-pedido-abrir
                    className="tarjeta-accion block w-full text-left"
                  >
                    {/* What was charged, rounding and all: the sale's total once it is read. */}
                    <div className="flex items-start justify-between gap-3">
                      <p className="line-clamp-2 min-w-0 font-medium break-words">{clienteDe(p)}</p>
                      <span className="tabular shrink-0 font-semibold">{formatMoney(venta?.total ?? totalConEnvio(p))}</span>
                    </div>
                    {/* Nothing said while the sales are read: «not on this phone» was only «not read yet». */}
                    {entregas && (
                      <div className="mt-1 flex flex-col gap-0.5 text-xs text-slate-500">
                        {venta ? (
                          <>
                            <p>{t('pedidos.entregadoEn', { ticket: venta.ticket, fecha: venta.fecha })}</p>
                            <p>{t('pedidos.cobroQuien', { quien, como: formasDeLaVenta(venta).map(formaDePago).join(', ') })}</p>
                          </>
                        ) : (
                          <p>{t('pedidos.entregadoSinVenta')}</p>
                        )}
                      </div>
                    )}
                  </button>
                </li>
              );
            }
            const marca = vista === 'pendientes' ? marcaDeEntrega(p.fechaEntrega, hoyEs) : null;
            const faltantes = vista === 'pendientes' && cortos ? (cortos.get(p.id) ?? []) : null;
            return (
              <li
                key={p.id}
                ref={p.id === tomado ? alTomado : undefined}
                className={`tarjeta ${p.id === tomado ? 'ring-2 ring-teal-500/50' : ''}`}
                data-pedido={p.id}
              >
                {/* Two lines for a long name before it is cut: the end of it tells two shops apart. */}
                <div className="flex items-start justify-between gap-3">
                  <p className="line-clamp-2 min-w-0 font-medium break-words">{clienteDe(p)}</p>
                  <span className="tabular shrink-0 font-semibold">{formatMoney(totalConEnvio(p))}</span>
                </div>
                {/* The day across the card, under the name and the amount, with the late and today's marked. */}
                <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                  {marca && (
                    <span className={`pastilla py-0.5 ${marca === 'atrasado' ? 'bg-rose-500/15 text-rose-200' : 'bg-teal-500/15 text-teal-100'}`}>
                      {t(marca === 'atrasado' ? 'pedidos.atrasado' : 'pedidos.hoy')}
                    </span>
                  )}
                  <span>{t('pos.entrega', { fecha: p.fechaEntrega, n: p.lineas.length })}</span>
                  {faltantes && (
                    <span
                      className={`pastilla py-0.5 ${faltantes.length > 0 ? 'bg-rose-500/15 text-rose-200' : 'bg-emerald-500/15 text-emerald-200'}`}
                      data-pedido-cobertura={faltantes.length > 0 ? 'corto' : 'apartado'}
                    >
                      {t(faltantes.length > 0 ? 'pedidos.corto' : 'pedidos.apartado')}
                    </span>
                  )}
                </p>
                {p.nota && <p className="mt-2 text-sm break-words text-slate-300">{p.nota}</p>}
                <ul className="mt-2 flex flex-col gap-0.5 text-xs text-slate-400">
                  {p.lineas.map((l, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      {/* Names are looked up, never snapshotted: a renamed product reads right. */}
                      <span className="truncate">
                        {formatQuantity(l.cantidad)} × {nombreDe(l.productoId)}
                      </span>
                      <span className="tabular shrink-0">{formatMoney(l.subtotal)}</span>
                    </li>
                  ))}
                  {(p.envio ?? 0) > 0 && (
                    <li className="flex justify-between gap-3" data-pedido-envio>
                      <span>{t('envio.campo')}</span>
                      <span className="tabular shrink-0">{formatMoney(p.envio!)}</span>
                    </li>
                  )}
                </ul>
                {faltantes && faltantes.length > 0 && (
                  <ul className="mt-2 flex flex-col gap-0.5 text-xs text-rose-300">
                    {faltantes.map((f) => (
                      <li key={f.productoId} data-pedido-falta={f.productoId}>
                        {t('pedidos.faltaDe', { cantidad: enSuUnidad(f.falta, f.productoId), producto: nombreDe(f.productoId) })}
                      </li>
                    ))}
                  </ul>
                )}
                {vista === 'cancelados' ? (
                  <p className="mt-2 text-sm break-words text-slate-400">
                    {t('pedidos.canceladoPor', { motivo: p.motivoCancelacion ?? '—' })}
                  </p>
                ) : (
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                    {/* To the till, loaded: there it is charged as any sale, and comes back here delivered. */}
                    {puedeCobrar && (
                      <Link
                        to="/pos"
                        search={{ pedido: p.id }}
                        className="btn-primario h-11 min-h-0 gap-1.5 px-4 text-sm"
                        data-guia="pedido-cobrar"
                      >
                        <Ico icono="cobrar" className="size-4" />
                        {t('pedidos.cobrar')}
                      </Link>
                    )}
                    {/* Before it is charged: to print and put in the bag, or send to the client. */}
                    <button
                      type="button"
                      className="btn-secundario h-11 min-h-0 gap-1.5 px-4 text-sm"
                      onClick={() => setRecibo(p.id)}
                      data-pedido-recibo
                    >
                      <Ico icono="imprimir" className="size-4" />
                      {t('pedidos.recibo')}
                    </button>
                    {/* On the screen it was taken on, as it stands: whoever takes orders changes them. */}
                    {puede && (
                      <Link
                        to="/clientes/pedidos/$pedidoId/editar"
                        params={{ pedidoId: p.id }}
                        className="btn-secundario h-11 min-h-0 gap-1.5 px-4 text-sm"
                        data-pedido-editar
                      >
                        <Ico icono="editar" className="size-4" />
                        {t('pedidos.editar')}
                      </Link>
                    )}
                    {puede && (
                      <button type="button" className="btn-texto gap-1 text-xs text-slate-500" onClick={() => setCancelando(p.id)}>
                        <Ico icono="cancelar" className="size-3.5" />
                        {t('pedidos.cancelar')}
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {vista !== 'pendientes' && encontrados.length > cuantos && (
        <button type="button" className="btn-secundario mt-4 w-full" onClick={() => setCuantos((n) => n + LOTE)}>
          <Ico icono="desplegar" />
          {t('comun.verMas')}
        </button>
      )}
      {enRecibo && (
        <ReciboDePedido
          pedido={enRecibo}
          cliente={clientes.find((c) => c.id === enRecibo.clienteId)}
          productos={productos}
          unidades={unidades}
          config={config}
          onCerrar={() => setRecibo(null)}
        />
      )}
      {abierto && (
        <DetallePedido
          pedidoId={abierto}
          productos={productos}
          unidades={unidades}
          usuarios={usuarios}
          config={config}
          onCerrar={() => setAbierto(null)}
        />
      )}
      {cancelando && (
        <PedirDato
          titulo={t('preguntar.cancelarPedido')}
          etiqueta={t('pedidos.porQueCancela')}
          maxLength={200}
          confirmar={t('pedidos.cancelar')}
          icono="cancelar"
          onCerrar={() => setCancelando(null)}
          onListo={(motivo) => {
            setNoSeCancelo(null);
            emitirYVer('pedido.cancelado', { pedidoId: cancelando, motivo }).then(
              ({ rechazo }) => setNoSeCancelo(rechazo),
              (e: unknown) => setNoSeCancelo(e instanceof Error ? e.message : String(e)),
            );
          }}
        />
      )}
    </Pagina>
  );
}

/**
 * An order's receipt before it is charged, in a sheet as the till's is after a sale: the text as it
 * prints, then WhatsApp, to the client's own chat when they have a number, and «Imprimir». Spanish
 * whatever the screen's language, as every receipt is: it is the customer's.
 */
function ReciboDePedido({
  pedido,
  cliente,
  productos,
  unidades,
  config,
  onCerrar,
}: {
  pedido: Pedido;
  cliente: Cliente | undefined;
  productos: readonly Producto[];
  unidades: readonly UnidadVenta[];
  config: Settings;
  onCerrar: () => void;
}) {
  const t = useT();
  const texto = textoPedido(pedido, datosNegocio(config), {
    ...(cliente ? { cliente: { nombre: cliente.nombre, direccion: cliente.direccion, telefono: cliente.telefono } } : {}),
    productos: new Map(productos.map((p) => [p.id, p.nombre])),
    presentaciones: new Map(unidades.map((u) => [u.id, { nombre: u.nombre, factor: u.factor }])),
  });
  return (
    <Modal
      // Who it is for is on the receipt, whole: a long name in the title was cut at a phone's width.
      titulo={t('pedidos.reciboTitulo')}
      onCerrar={onCerrar}
      pie={
        <div className="flex gap-2">
          <a
            className="btn-secundario min-w-0 flex-1 px-3 text-sm"
            href={enlaceWhatsApp(texto, cliente?.telefono ?? undefined)}
            target="_blank"
            rel="noreferrer"
          >
            <Ico icono="whatsapp" />
            WhatsApp
          </a>
          <button type="button" className="btn-primario min-w-0 flex-1 px-3 text-sm" onClick={() => imprimirTexto(texto)}>
            <Ico icono="imprimir" />
            {t('comun.imprimir')}
          </button>
        </div>
      }
    >
      <p className="mb-3 text-sm text-slate-400">{t('pedidos.reciboSinCobrar')}</p>
      <pre className="tabular overflow-x-auto rounded-xl bg-slate-950 p-4 text-xs leading-relaxed whitespace-pre" data-recibo-pedido>
        {texto}
      </pre>
    </Modal>
  );
}

/** A line of one base unit each: the bottle, the pound. Anything else is a presentation. */
const UNA_UNIDAD = fromUnits(1);

/**
 * The event that recorded an order or a sale: who did it, and the moment, which the rows keep only
 * as a day. The latest first, as the sale's moment is found in `Ventas.tsx`: what somebody opens is
 * nearly always recent. One this phone refused never happened.
 */
async function eventoDe(tipo: 'pedido.tomado' | 'pedido.actualizado' | 'venta.registrada', campo: 'pedidoId' | 'ventaId', id: string) {
  return db.eventos
    .where('type')
    .equals(tipo)
    .reverse()
    .filter((e) => !e.rechazadoAqui && e.estado !== 'rechazado' && (e.payload as Record<string, unknown>)[campo] === id)
    .first();
}

/**
 * A delivered order, opened (the owner, 2026-10-14: «on the orders page, in the delivered tab, you
 * should be able to click every order and see all the details relevant to that clicked order»).
 *
 * Who it was for, the name whole, with their phone and address. The order as it was taken: when and
 * by whom, and when and by whom it was last changed, if it was (2026-10-15), for which day, at which
 * list, the note, each line at the price agreed with why and who allowed it, the delivery and what
 * it all came to. Then the sale that delivered it: its ticket,
 * when, by whom and where it was charged, how many days after the day agreed, whether it was
 * charged as ordered or what was charged instead, the delivery, rounding and total, each payment and
 * the change, what came back since and how it was refunded, and for the owner what it left.
 *
 * «Ver la venta» opens the sale's own sheet in place of this one (its receipt, a return, a void),
 * and closing it comes back here. Its sale voided, the order is to deliver again: it leaves this
 * list, and its sheet closes.
 */
function DetallePedido({
  pedidoId,
  productos,
  unidades,
  usuarios,
  config,
  onCerrar,
}: {
  pedidoId: string;
  productos: readonly Producto[];
  unidades: readonly UnidadVenta[];
  usuarios: readonly Usuario[];
  config: Settings;
  onCerrar: () => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const verCostos = usePuede('ver_costos');
  const [enLaVenta, setEnLaVenta] = useState(false);
  const datos = useLiveQuery(async () => {
    const pedido = await db.pedidos.get(pedidoId);
    if (!pedido) return null;
    const venta = pedido.ventaId ? await db.ventas.get(pedido.ventaId) : undefined;
    return {
      pedido,
      venta,
      cliente: await db.clientes.get(pedido.clienteId),
      lista: await db.listas.get(pedido.listaId),
      tomado: await eventoDe('pedido.tomado', 'pedidoId', pedido.id),
      cambiado: await eventoDe('pedido.actualizado', 'pedidoId', pedido.id),
      cobrado: venta ? await eventoDe('venta.registrada', 'ventaId', venta.id) : undefined,
      devoluciones: venta ? (await db.devoluciones.where('ventaId').equals(venta.id).toArray()).sort((a, b) => a.fecha.localeCompare(b.fecha)) : [],
    };
  }, [pedidoId]);
  // Undefined while it is read; gone, or no longer delivered, it has nothing to show here.
  const sigue = datos === undefined || (datos !== null && datos.pedido.estado === 'entregado');
  useEffect(() => {
    if (!sigue) onCerrar();
  }, [sigue, onCerrar]);
  if (!datos || !sigue) return null;
  const { pedido, venta, cliente, lista, tomado, cambiado, cobrado, devoluciones } = datos;
  if (enLaVenta && venta) return <DetalleVenta ventaId={venta.id} negocio={datosNegocio(config)} onCerrar={() => setEnLaVenta(false)} />;

  const producto = new Map(productos.map((p) => [p.id, p]));
  const presentacion = new Map(unidades.map((u) => [u.id, u]));
  const quien = (id: string | null | undefined) => (id ? usuarios.find((u) => u.id === id)?.nombre : undefined) ?? t('ventas.alguien');
  const nombreDe = (productoId: string | null, descripcion: string) => {
    const p = productoId ? producto.get(productoId) : undefined;
    return p ? nombre(p) : descripcion;
  };
  const lugar = (ubicacion: string) => {
    const id = idDeCamion(ubicacion);
    const camion = id ? config.camiones.find((c) => c.id === id) : undefined;
    return camion ? `${etiquetaLugar(ubicacion)} ${camion.nombre}` : etiquetaLugar(ubicacion);
  };
  /** A payment as the sale took it: the way, the dollars and their rate, the reference, and whether it arrived. */
  const comoSePago = (p: VentaPago) =>
    [
      t(claveDeForma(p.forma)),
      p.montoOriginal
        ? t('cobro.dolaresA', { monto: formatMoney(p.montoOriginal, { symbol: false }), tasa: formatFxRate(fxRate(p.tasaCambio ?? config.fxTasaTienda)) })
        : null,
      p.referencia,
      p.estado === 'pendiente' ? t('cobro.sinConfirmar') : p.estado === 'rechazado' ? t('pedidos.noLlego') : null,
    ]
      .filter(Boolean)
      .join(' · ');
  /**
   * When it was, as the event that recorded it says: the moment, when that was the day the order or
   * the sale says; the day alone when not, as for an order typed in a day late or one of the practice
   * shop's, dated before it was built.
   */
  const cuando = (fecha: string, e: { clientTs: string } | undefined) =>
    e && fechaEnNicaragua(e.clientTs) === fecha ? instante(e.clientTs) : diaDeLista(fecha);
  const reembolso = (d: Devolucion) =>
    d.reembolso === 'credito' ? t('pedidos.reembolsoACuenta') : t('pedidos.reembolsoEn', { como: t(claveDeForma(d.reembolso)) });
  const atraso = venta ? diasDeAtraso(pedido, venta) : 0;

  return (
    <Modal
      // Who it is for is the first thing in it, whole: a long name in the title was cut at a phone's width.
      titulo={t('pedidos.detalleTitulo')}
      onCerrar={onCerrar}
      pie={
        venta && (
          <button type="button" className="btn-secundario w-full" onClick={() => setEnLaVenta(true)} data-pedido-ver-venta>
            <Ico icono="ventas" />
            {t('pedidos.verVenta')}
          </button>
        )
      }
    >
      <div data-pedido-detalle={pedido.id}>
        <p className="font-medium break-words" data-detalle-cliente>
          {cliente?.nombre ?? t('pedidos.clienteDesconocido')}
        </p>
        {(cliente?.telefono || cliente?.direccion) && (
          <p className="mt-0.5 text-sm break-words text-slate-400" data-detalle-contacto>
            {[cliente.telefono, cliente.direccion].filter(Boolean).join(' · ')}
          </p>
        )}

        <h3 className="mt-5 mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">{t('pedidos.elPedido')}</h3>
        <dl className="flex flex-col gap-1" data-detalle-pedido>
          <Par
            etiqueta={t('pedidos.tomado')}
            valor={tomado ? `${cuando(pedido.fecha, tomado)} · ${quien(tomado.actorId)}` : diaDeLista(pedido.fecha)}
          />
          {cambiado && (
            <Par etiqueta={t('pedidos.cambiado')} valor={`${instante(cambiado.clientTs)} · ${quien(cambiado.actorId)}`} marca="cambiado" />
          )}
          <Par etiqueta={t('pedidos.paraEl')} valor={diaDeLista(pedido.fechaEntrega)} />
          {lista && <Par etiqueta={t('pedidos.lista')} valor={lista.nombre} />}
        </dl>
        {pedido.nota && (
          <p className="mt-2 rounded-lg bg-slate-950/40 px-3 py-2 text-sm break-words text-slate-300" data-detalle-nota>
            {pedido.nota}
          </p>
        )}
        <ul className="mt-1 flex flex-col divide-y divide-slate-800" data-detalle-lineas="pedido">
          {pedido.lineas.map((l, i) => (
            <Linea
              key={i}
              producto={producto.get(l.productoId)}
              descripcion={t('pedidos.productoNoEncontrado')}
              cantidad={l.cantidad}
              unidad={presentacion.get(l.unidadVentaId)}
              precio={l.precioUnitario}
              subtotal={l.subtotal}
              nota={[l.motivoOverride, l.autorizadoPor && t('pedidos.autorizo', { nombre: quien(l.autorizadoPor) })].filter(Boolean).join(' · ')}
            />
          ))}
        </ul>
        <div className="flex flex-col gap-1 border-t border-slate-800 pt-3" data-detalle-total-pedido>
          {(pedido.envio ?? 0) > 0 && <Fila etiqueta={t('ventas.envio')} valor={formatMoney(pedido.envio!, { symbol: false })} />}
          <Fila etiqueta={t('pedidos.totalDelPedido')} valor={formatMoney(totalConEnvio(pedido))} fuerte />
        </div>

        <h3 className="mt-6 mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">{t('pedidos.laEntrega')}</h3>
        {!venta ? (
          <p className="text-sm text-slate-400">{t('pedidos.entregadoSinVenta')}</p>
        ) : (
          <>
            <dl className="flex flex-col gap-1" data-detalle-entrega>
              <Par etiqueta={t('pedidos.ticket')} valor={venta.ticket} />
              <Par
                etiqueta={t('pedidos.cobrado')}
                valor={cobrado ? `${cuando(venta.fecha, cobrado)} · ${quien(cobrado.actorId)}` : diaDeLista(venta.fecha)}
              />
              <Par etiqueta={t('pedidos.donde')} valor={lugar(venta.ubicacion)} />
            </dl>
            {atraso > 0 && (
              <p className="mt-1 text-xs text-amber-300" data-detalle-atraso>
                {t('pedidos.atraso', { n: atraso })}
              </p>
            )}
            {cobradoComoSePidio(pedido, venta) ? (
              <p className="mt-2 text-sm text-slate-400" data-detalle-como-se-pidio>
                {t('pedidos.comoSePidio')}
              </p>
            ) : (
              <>
                <p className="mt-2 text-sm text-amber-300" data-detalle-distinto>
                  {t('pedidos.distintoDeLoPedido')}
                </p>
                <ul className="mt-1 flex flex-col divide-y divide-slate-800" data-detalle-lineas="venta">
                  {venta.lineas.map((l, i) => (
                    <Linea
                      key={i}
                      producto={l.productoId ? producto.get(l.productoId) : undefined}
                      descripcion={l.descripcion}
                      cantidad={l.cantidad}
                      unidad={l.unidadVentaId ? presentacion.get(l.unidadVentaId) : undefined}
                      precio={l.precioUnitario}
                      subtotal={l.subtotal}
                      devuelta={l.devuelta}
                      nota={[
                        l.precioUnitario !== l.precioLista && `${t('pos.lista')} ${formatMoney(l.precioLista, { symbol: false })}`,
                        l.motivoOverride,
                        l.autorizadoPor && t('pedidos.autorizo', { nombre: quien(l.autorizadoPor) }),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  ))}
                </ul>
              </>
            )}
            <div className="mt-2 flex flex-col gap-1 border-t border-slate-800 pt-3" data-detalle-cobro>
              {venta.descuento > 0 && <Fila etiqueta={t('ventas.descuento')} valor={`-${formatMoney(venta.descuento, { symbol: false })}`} />}
              {(venta.envio ?? 0) > 0 && <Fila etiqueta={t('ventas.envio')} valor={formatMoney(venta.envio!, { symbol: false })} />}
              {venta.redondeo !== 0 && (
                <Fila etiqueta={t('ventas.redondeo')} valor={formatMoney(venta.redondeo, { symbol: false, signed: true })} />
              )}
              <Fila etiqueta={t('pedidos.totalCobrado')} valor={formatMoney(venta.total)} fuerte />
              {venta.pagos.map((p, i) => (
                <Fila key={i} etiqueta={comoSePago(p)} valor={formatMoney(p.monto, { symbol: false })} />
              ))}
              {venta.cambio > 0 && <Fila etiqueta={t('cobro.vuelto')} valor={formatMoney(venta.cambio, { symbol: false })} />}
              {(venta.devuelto ?? 0) > 0 && (
                <Fila etiqueta={t('ventas.devuelto')} valor={`-${formatMoney(venta.devuelto!, { symbol: false })}`} />
              )}
              {verCostos && (
                <span data-detalle-utilidad>
                  <Fila etiqueta={t('ventas.utilidadBruta')} valor={formatMoney(utilidadDeVenta(venta))} />
                </span>
              )}
            </div>
            {devoluciones.length > 0 && (
              <>
                <h3 className="mt-6 mb-1 text-xs font-semibold tracking-wide text-slate-500 uppercase">{t('pedidos.devoluciones')}</h3>
                <ul className="flex flex-col divide-y divide-slate-800" data-detalle-devoluciones>
                  {devoluciones.map((d) => (
                    <li key={d.id} className="py-2.5" data-detalle-devolucion={d.id}>
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="min-w-0 text-sm break-words">
                          {diaDeLista(d.fecha)} ·{' '}
                          {d.lineas.map((l) => `${formatQuantity(l.cantidad)} × ${nombreDe(l.productoId, l.descripcion)}`).join(', ')}
                        </p>
                        <span className="tabular shrink-0 text-sm">-{formatMoney(d.total, { symbol: false })}</span>
                      </div>
                      <p className="mt-0.5 text-xs break-words text-slate-500">
                        {d.motivo} · {reembolso(d)}
                      </p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/** A line of the order or of its sale: the picture, the name, so many at so much, why, and what it came to. */
function Linea({
  producto,
  descripcion,
  cantidad,
  unidad,
  precio,
  subtotal,
  nota,
  devuelta,
}: {
  producto: Producto | undefined;
  /** What it is called with no product behind it: a quick sale as typed, or a product gone. */
  descripcion: string;
  cantidad: Milli;
  unidad: UnidadVenta | undefined;
  precio: Centavos;
  subtotal: Centavos;
  /** Why its price is not the list's, and who allowed it, as the order or the sale kept them. */
  nota: string;
  devuelta?: Milli;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  return (
    <li className="flex items-center gap-3 py-2.5">
      {producto ? <Miniatura producto={producto} tamano="sm" /> : <MiniaturaIcono icono="rapido" tamano="sm" />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">{producto ? nombre(producto) : descripcion}</p>
        <p className="tabular mt-0.5 text-xs text-slate-500">
          {formatQuantity(cantidad)}
          {/* The case, when it was a case: «1 Fardo × 480.00», not a bottle at the price of six. */}
          {unidad && unidad.factor !== UNA_UNIDAD && <> {etiquetaUnidad(unidad.nombre, unidad.nombre)}</>} ×{' '}
          {formatMoney(precio, { symbol: false })}
          {(devuelta ?? 0) > 0 && <span className="ml-2 text-sky-300">{t('ventas.devueltas', { n: formatQuantity(devuelta!) })}</span>}
        </p>
        {nota && <p className="mt-0.5 text-xs break-words text-amber-400">{nota}</p>}
      </div>
      <span className="tabular shrink-0 text-sm">{formatMoney(subtotal, { symbol: false })}</span>
    </li>
  );
}

/** A name and what it says, the words wrapping on the right: a moment and a person can be long. */
function Par({ etiqueta, valor, marca }: { etiqueta: string; valor: string; /** For the browser checks. */ marca?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4" {...(marca ? { 'data-detalle-par': marca } : {})}>
      <dt className="shrink-0 text-sm text-slate-400">{etiqueta}</dt>
      <dd className="min-w-0 text-right text-sm wrap-break-word">{valor}</dd>
    </div>
  );
}

/** A figure beside its name, as a sale's and a purchase's sheets have them. */
function Fila({ etiqueta, valor, fuerte }: { etiqueta: string; valor: string; fuerte?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className={`min-w-0 text-sm ${fuerte ? 'font-medium' : 'text-slate-400'}`}>{etiqueta}</span>
      <span className={`tabular shrink-0 ${fuerte ? 'text-lg font-semibold' : 'text-sm'}`}>{valor}</span>
    </div>
  );
}
